import { useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";
import Table from "react-bootstrap/Table";
import "./rolelisting.scss";
import threedotvertical from "../../../assets/images/three-dots-vertical.svg";
import editicon from "../../../assets/images/edit-icon.svg";
import deleteicon from "../../../assets/images/delete-icon.svg";
import deactivateicon from "../../../assets/images/deactivate-icon.svg";
import duplicatePlusIcon from "../../../assets/images/duplicate-plus-icon.svg";
import { ReactSVG } from "react-svg";
import { toggleClass } from "../../../utils/commonUtils";
import useRolesAndPermissions from "../../../Hooks/useRolesAndPermissions";
import Loading from "../../../components/LoadingPage/Loading";
import CommonSearchOnly from "../../../components/commonSearchOnly/commonSearchOnly";
import { DeleteConfModel } from "../../../components/DeleteConfModel/deleteConfModel";
import { useAppSelector } from "../../../redux/hooks";
import { selectCurrentUserId } from "../../../redux/auth/authSlice";
import { CommonDuplicateModel } from "../../../components/commonDuplicateModel/duplicateModel";
import { useModulePermissions, PermissionModule, PermissionIdentifier } from "../../../utils/permissionUtils";
import { useNavigate } from "react-router-dom";

const RoleListing = () => {
  const navigate = useNavigate();
  const { getRolesAPI, getRolesLoading, deleteRolesPermissionAPI, deleteRolesPermissionLoading, duplicateRolePermissionAPI, duplicateRolesPermissionLoading, setPermissionActiveAPI, setPermissionActiveLoading, setPermissionInActiveAPI, setPermissionDeActiveLoading } = useRolesAndPermissions();
  const currentUserId = useAppSelector(selectCurrentUserId);
  const [rolesList, setRolesList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const defaultFilter = { searchText: "" };
  const [currentRole, setCurrentRole] = useState<any>();
  const [searchFieldVal, setSearchFieldVal] = useState(defaultFilter);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);

  const toggleDuplicateModal = () => {
    toggleClass();
    setShowDuplicateModal(!showDuplicateModal);
  };

  const toggleModal = () => {
    toggleClass();
    setShowModal(!showModal);
  };

  const getRoles = () => {
    getRolesAPI(searchFieldVal)
      .then((res: any) => setRolesList(res))
      .catch();
  };

  useEffect(() => {
    getRoles();
  }, [searchFieldVal]);

  const deleteRole = async () => {
    await deleteRolesPermissionAPI({
      id: currentRole.id,
      deletedBy: currentUserId ?? "",
    });
    toggleModal();
    getRoles();
  };

  const duplicateRole = async () => {
    await duplicateRolePermissionAPI({ id: currentRole.id, createdBy: currentUserId ?? "" });
    toggleDuplicateModal();
    getRoles();
  };

  const changePermissionStatus = async (role: any) => {
    if (role.isActive) {
      await setPermissionInActiveAPI({ id: role.id, updatedBy: currentUserId ?? "" });
    } else {
      await setPermissionActiveAPI({ id: role.id, updatedBy: currentUserId ?? "" });
    }
    getRoles();
  }

  const isPermitted = useModulePermissions(PermissionModule.ROLES_AND_PERMISSION);
  const isCreatePermitted = isPermitted(PermissionIdentifier.CREATE_ROLES_AND_PERMISSION);
  const isEditPermitted = isPermitted(PermissionIdentifier.EDIT_ROLES_AND_PERMISSION);
  const isDeletePermitted = isPermitted(PermissionIdentifier.DELETE_ROLES_AND_PERMISSION);
  const isDuplicatePermitted = isPermitted(PermissionIdentifier.DUPLICATE_ROLES_AND_PERMISSION);
  const isStatusChangePermitted = isPermitted(PermissionIdentifier.INACTIVE_ROLES_AND_PERMISSION);
  const isAnyActionPermitted = isEditPermitted || isDeletePermitted || isDuplicatePermitted || isStatusChangePermitted;

  return (
    <div className="role-listing-wrapper">
      {getRolesLoading || deleteRolesPermissionLoading || duplicateRolesPermissionLoading || setPermissionActiveLoading || setPermissionDeActiveLoading ? <Loading /> : null}

      <CommonSearchOnly
        title="Roles & Permissions"
        buttonTitle="Add Role"
        href="/add-role"
        disRecords={rolesList.length || 0}
        totalRecords={rolesList.length || 0}
        searchVal={searchFieldVal}
        setSearchVal={setSearchFieldVal}
        defaultFilter={defaultFilter}
        buttonPermission={isCreatePermitted}
      />

      <div className="listing-table-wrapper">
        <Table responsive="md" hover={true}>
          <thead>
            <tr>
              <th>Role Name</th>
              <th>Description</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rolesList?.map((x: any) => (
              <tr key={x.id} onDoubleClick={() => navigate(`/edit-role/${x.id}`)}>
                <td>{x.roleName}</td>
                <td>{x.roleDescription}</td>
                <td key={x.id}>
                  <Dropdown>
                    {isAnyActionPermitted && (
                      <Dropdown.Toggle variant="link" id="dropdown-basic">
                        <ReactSVG
                          className="svg-box"
                          wrapper="span"
                          src={threedotvertical}
                        />
                      </Dropdown.Toggle>
                    )}
                    <Dropdown.Menu>
                      {isEditPermitted && (
                        <Dropdown.Item onClick={() => navigate(`/edit-role/${x.id}`)}>
                          <ReactSVG
                            className="svg-box"
                            wrapper="span"
                            src={editicon}
                          />
                          Edit
                        </Dropdown.Item>
                      )}
                      {isDeletePermitted && (
                        <Dropdown.Item
                          onClick={() => {
                            setCurrentRole(x);
                            toggleDuplicateModal();
                          }}
                        >
                          <ReactSVG
                            className="svg-box"
                            wrapper="span"
                            src={duplicatePlusIcon}
                          />
                          Duplicate
                        </Dropdown.Item>
                      )}
                      {isDeletePermitted && (
                        <Dropdown.Item
                          onClick={() => {
                            setCurrentRole(x);
                            toggleModal();
                          }}
                        >
                          <ReactSVG
                            className="svg-box"
                            wrapper="span"
                            src={deleteicon}
                          />
                          Delete
                        </Dropdown.Item>
                      )}
                      {isStatusChangePermitted && (
                        <Dropdown.Item
                          onClick={() => changePermissionStatus(x)}
                          style={currentUserId === x.id
                            ? {
                              pointerEvents: "none",
                              opacity: 0.5,
                            }
                            : {}
                          }
                        >
                          <ReactSVG
                            className="svg-box"
                            wrapper="span"
                            src={deactivateicon}
                          />
                          {x.isActive ? "Deactivate" : "Activate"}
                        </Dropdown.Item>
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <DeleteConfModel
        showModal={showModal}
        toggleModal={toggleModal}
        deleteAction={deleteRole}
        recordName={currentRole?.roleName || ""}
        modelName={`Role`}
        isDeletePermitted={isDeletePermitted}
      />
      <CommonDuplicateModel
        showDuplicateModal={showDuplicateModal}
        toggleDuplicateModal={toggleDuplicateModal}
        modelName={`Role`}
        recordName={currentRole?.roleName ?? ""}
        duplicate={duplicateRole}
        isDuplicatePermitted={isDuplicatePermitted}
      />
    </div>
  );
};

export default RoleListing;
